import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { Renderer } from "@lib/titan2/dist/game/renderer/Renderer";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { SpineSprite } from "pixi-spine";
import { DisplayObject } from "pixi.js";
export function addToTitanSpineSlot(spine, displayObject, slotName) {
    addToSpineSlot(spine.getSpine(), displayObject, slotName);
}
export function addToSpineSlot(spine, displayObject, slotName) {
    const slotIndex = spine.skeleton.findSlotIndex(slotName);
    if (slotIndex >= 0) {
        spine.children[slotIndex].addChild(displayObject);
    }
    else {
        throw new Error(`addToSpineSlot - Slot not found: ` + slotName);
    }
}
export function getTitanSpineSlot(spine, slotName) {
    return getSpineSlot(spine.getSpine(), slotName);
}
export function getSpineSlot(spine, slotName) {
    const slotIndex = spine.skeleton.findSlotIndex(slotName);
    if (slotIndex >= 0) {
        return spine.children[slotIndex];
    }
    else {
        throw new Error(`getSpineSlot - Slot not found: ` + slotName);
    }
}
export function hasTitanSpineSlot(spine, slotName) {
    try {
        getSpineSlot(spine.getSpine(), slotName);
        return true;
    }
    catch (e) {
        return false;
    }
}
export function updateResponsiveSpineSlotsChildren(spine, slotName) {
    if (spine.responsive === false)
        return;
    const currentOrientation = Renderer.getInstance().getOrientation();
    const otherOrientation = currentOrientation === OrientationType.LANDSCAPE ? OrientationType.PORTRAIT : OrientationType.LANDSCAPE;
    const fromSpine = spine.getSpine(otherOrientation);
    const toSpine = spine.getSpine(currentOrientation);
    const slotNames = slotName !== undefined ? [slotName] : fromSpine.skeleton.slots.map(slot => slot.data.name);
    slotNames.forEach(_slotName => {
        const fromSlot = getSpineSlot(fromSpine, _slotName);
        const toSlot = getSpineSlot(toSpine, _slotName);
        if (fromSlot.mask && fromSlot.mask instanceof DisplayObject && !toSlot.mask) {
            const mask = fromSlot.mask;
            toSlot.mask = mask;
            toSlot.addChild(mask);
            fromSlot.mask = undefined;
        }
        fromSlot
            .children
            .filter(child => (child instanceof TitanSpine) || (child instanceof TitanText) || (child instanceof TitanSprite))
            .forEach(child => getSpineSlot(toSpine, _slotName).addChild(child));
    });
}
export function clearResponsiveSpineSlotsChildren(spine, slotName) {
    const currentOrientation = Renderer.getInstance().getOrientation();
    const otherOrientation = currentOrientation === OrientationType.LANDSCAPE ? OrientationType.PORTRAIT : OrientationType.LANDSCAPE;
    const spineA = spine.getSpine(currentOrientation);
    const spineB = spine.getSpine(otherOrientation);
    const slotNames = slotName !== undefined ? [slotName] : spineA.skeleton.slots.map(slot => slot.data.name);
    slotNames.forEach(_slotName => {
        (getSpineSlot(spineA, _slotName).children).concat(getSpineSlot(spineB, _slotName).children)
            .filter(child => !(child instanceof SpineSprite)) // do NOT move SpineSprite instances as the Spine will get very confused and probably not render correctly. Only move other types of instances we've added at runtime.
            .forEach(child => child.destroy());
    });
}
export function resetTitanSpine(spine) {
    if (spine.getSpine(OrientationType.LANDSCAPE)) {
        spine.getSpine(OrientationType.LANDSCAPE).state.clearTracks();
        spine.getSpine(OrientationType.LANDSCAPE).skeleton.setToSetupPose();
    }
    if (spine.getSpine(OrientationType.PORTRAIT)) {
        spine.getSpine(OrientationType.PORTRAIT).state.clearTracks();
        spine.getSpine(OrientationType.PORTRAIT).skeleton.setToSetupPose();
    }
}
export function getDuration(spine, animation) {
    const spineData = spine.getSpine()?.spineData;
    if (spineData) {
        const animNames = Array.isArray(animation)
            ? animation.slice()
            : [animation];
        const animations = spineData.animations.filter((anim) => animNames.indexOf(anim.name) > -1);
        if (animations.length > 1) {
            return animations.map((anim) => anim.duration);
        }
        else if (animations.length === 1) {
            return animations[0].duration;
        }
    }
    return null;
}
export function skipToEnd(spine, trackIndex = 0) {
    const track = spine.getSpine()?.state.tracks[trackIndex];
    const duration = track.animation.duration;
    track.time = duration;
    spine.getSpine()?.update(0.01);
}
export function toTween(spine, animation, track = 0) {
    return gsap.to({}, {
        onStart: () => {
            SpineUtil.play(spine, animation, undefined, undefined, track);
        },
        duration: getDuration(spine, animation)
    });
}
export async function blockUntil(obj, event) {
    return new Promise(resolve => {
        obj.registerGame(event, (recievedEvent) => {
            obj.unregisterGame(event);
            resolve(recievedEvent);
        });
    });
}
export async function delayFor(timeInSeconds) {
    return new Promise(resolve => {
        gsap.delayedCall(timeInSeconds, resolve);
    });
}
export function SpineComplete(spine, track = 0) {
    return new Promise((resolve) => {
        const eventListener = {
            complete: (entry) => {
                if (entry.trackIndex === track) {
                    spine.getSpine(OrientationType.LANDSCAPE).state.removeListener(eventListener);
                    spine.getSpine(OrientationType.PORTRAIT).state.removeListener(eventListener);
                    resolve();
                }
            },
        };
        spine.getSpine(OrientationType.LANDSCAPE).state.addListener(eventListener);
        spine.getSpine(OrientationType.PORTRAIT).state.addListener(eventListener);
    });
}
export function SpineEvent(spine, name, track = 0) {
    return new Promise((resolve) => {
        const eventListener = {
            event: (entry, event) => {
                if (entry.trackIndex === track && event.data.name === name) {
                    spine.getSpine(OrientationType.LANDSCAPE).state.removeListener(eventListener);
                    spine.getSpine(OrientationType.PORTRAIT).state.removeListener(eventListener);
                    resolve();
                }
            },
        };
        spine.getSpine(OrientationType.LANDSCAPE).state.addListener(eventListener);
        spine.getSpine(OrientationType.PORTRAIT).state.addListener(eventListener);
    });
}
export function framesToSeconds(frames, fps = 30) {
    return frames / fps;
}
//# sourceMappingURL=SpineUtils.js.map